import { keepPreviousData, queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { getErrorMessage } from '@/lib/api-errors'
import { toast } from '@/lib/toast'

import { createEntity, getEntities, getEntityStats } from '../api/entityApi'

import type { PeriodFilter } from '../types/entity.types'

export const entityStatsQueryOptions = (period: PeriodFilter) =>
  queryOptions({
    queryKey: ['entity-stats', period] as const,
    queryFn: () => getEntityStats(),
    staleTime: 1000 * 60 * 5,
  })

export const entitiesQueryOptions = () =>
  queryOptions({
    queryKey: ['entities'] as const,
    queryFn: () => getEntities(),
    staleTime: 1000 * 60 * 2,
    placeholderData: keepPreviousData,
  })

export function useEntityStats(period: PeriodFilter) {
  return useQuery(entityStatsQueryOptions(period))
}

export function useEntities() {
  return useQuery(entitiesQueryOptions())
}

export function useCreateEntity() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: createEntity,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['entities'] })
      queryClient.invalidateQueries({ queryKey: ['entity-stats'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
      toast.success('Entity created successfully.')
    },
    onError: (err) => toast.error(getErrorMessage(err, 'Failed to create entity.')),
  })
}
